import { createContext, useContext, useEffect } from "react";
import { translations, localePath } from "./translations";

const htmlLang = { en: "en", sv: "sv", da: "da", nb: "nb", fi: "fi", is: "is", de: "de" };
const ogLocale = { en: "en_US", sv: "sv_SE", da: "da_DK", nb: "nb_NO", fi: "fi_FI", is: "is_IS", de: "de_DE" };
const SITE = "https://nowi.digital";

const LocaleContext = createContext({ locale: "en", t: translations.en });

function setMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export const LocaleProvider = ({ locale = "en", seoTitle, seoDescription, canonicalPath, ogImage, children }) => {
  const t = translations[locale] || translations.en;

  useEffect(() => {
    const title = seoTitle || `NOWI — ${t.hero.line1} ${t.hero.line2}`;
    const description = seoDescription || t.hero.subtitle;
    const path = canonicalPath != null ? canonicalPath : (localePath[locale] || "/");
    const url = `${SITE}${path}`;
    const image = ogImage ? `${SITE}${ogImage}` : `${SITE}/og-image.jpg`;

    document.documentElement.lang = htmlLang[locale] || "en";
    document.title = title;

    setMeta("name", "description", description);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:locale", ogLocale[locale] || "en_US");
    setMeta("property", "og:image", image);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image);
    if (ogImage) {
      setMeta("property", "og:image:width", "1200");
      setMeta("property", "og:image:height", "630");
    }
    setLink("canonical", url);
  }, [locale, t, seoTitle, seoDescription, canonicalPath, ogImage]);

  return <LocaleContext.Provider value={{ locale, t }}>{children}</LocaleContext.Provider>;
};

export const useLocale = () => useContext(LocaleContext);
